import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import ProjectCard from "./ProjectCards";
import projects from "./projectData";
import { FiSearch, FiFilter, FiGrid, FiList } from "react-icons/fi";
import '../../style.css';
function Projects() {
  const [visibleCards, setVisibleCards] = useState([]);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState(projects);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [isLoading, setIsLoading] = useState(true);
  const searchRef = useRef(null);
  const projectsRef = useRef(null);

  // Filter projects based on type
  const filterProjects = (filter) => {
    setActiveFilter(filter);
    setVisibleCards([]);
    
    let filtered;
    if (filter === 'all') {
      filtered = projects;
    } else if (filter === 'apps') {
      filtered = projects.filter(project => project.isApp);
    } else if (filter === 'web') {
      filtered = projects.filter(project => !project.isApp);
    }
    
    setFilteredProjects(filtered);
    
    // Re-animate cards
    setTimeout(() => {
      filtered.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 150);
      });
    }, 100);
  };

  // Search functionality
  const handleSearch = (term) => {
    setSearchTerm(term);
    const filtered = projects.filter(project => 
      project.title.toLowerCase().includes(term.toLowerCase()) ||
      project.description.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredProjects(filtered);
    setVisibleCards([]);
    
    // Re-animate filtered results
    setTimeout(() => {
      filtered.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 100);
      });
    }, 100);
  };

  useEffect(() => {
    // Loading sequence
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    // Animate header first
    const headerTimer = setTimeout(() => {
      setHeaderVisible(true);
    }, 700);

    // Animate cards with staggered effect
    const cardsTimer = setTimeout(() => {
      filteredProjects.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 200);
      });
    }, 1200);

    return () => {
      clearTimeout(loadingTimer);
      clearTimeout(headerTimer);
      clearTimeout(cardsTimer);
    };
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('.project-card-container');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [filteredProjects]);

  return (
    <Container fluid className="project-section">
      {/* Loading Screen */}
      {isLoading && (
        <div className="projects-loader">
          <div className="loader-content">
            <div className="loader-spinner"></div>
            <h3>Loading Amazing Projects...</h3>
          </div>
        </div>
      )}
      
      <Container>
        {/* Floating Particles Background */}
        <div className="floating-particles">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className="particle" 
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${10 + Math.random() * 20}s`
              }}
            ></div>
          ))}
        </div>

        <div className={`project-header ${headerVisible ? 'animate-in' : ''}`}>
          <span className="project-heading">
            My Recent <strong className="lgBlue">Works</strong>
          </span>
          {/* Advanced Controls */}
        <div className={`project-controls ${headerVisible ? 'controls-animate-in' : ''}`}>
         

         {/* Filter Buttons */}
         <div className="filter-container">
           <div className="filter-buttons">
             {[
               { key: 'all', label: 'All Projects' },
               { key: 'apps', label: 'Mobile Apps' },
               { key: 'web', label: 'Web Apps' }
             ].map((filter) => (
               <button
                 key={filter.key}
                 className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
                 onClick={() => filterProjects(filter.key)}
               >
                 {filter.label}
                 <span className="filter-count">
                   {filter.key === 'all' 
                     ? projects.length 
                     : filter.key === 'apps' 
                     ? projects.filter(p => p.isApp).length
                     : projects.filter(p => !p.isApp).length
                   }
                 </span>
               </button>
             ))}
           </div>
         </div>

         {/* View Mode Toggle */}
         <div className="view-toggle">
           <button 
             className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
             onClick={() => setViewMode('grid')}
           >
             <FiGrid />
           </button>
           <button 
             className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
             onClick={() => setViewMode('list')}
           >
             <FiList />
           </button>
         </div>
       </div>
          
        </div>
        
       
        
        {/* Projects Grid */}
        <div ref={projectsRef} className={`projects-container ${viewMode}`}>
          {filteredProjects.length === 0 ? (
            <div className="no-projects">
              <div className="no-projects-icon">🔍</div>
              <h3>No projects found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <Row className="d-flex justify-content-center projects-grid">
              {filteredProjects.map((project, index) => (
                <Col
                  md={viewMode === 'list' ? 12 : 6}
                  lg={viewMode === 'list' ? 10 : 5}
                  className={`project-card-container ${viewMode}-view ${
                    visibleCards.includes(index) ? 'card-animate-in' : 'card-hidden'
                  }`}
                  key={`${project.title}-${index}`}
                  style={{
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <ProjectCard
                    isApp={project.isApp}
                    title={project.title}
                    description={project.description}
                    ghLink={project.ghLink}
                    demoLink={project.demoLink}
                    index={index}
                    viewMode={viewMode}
                  />
                </Col>
              ))}
            </Row>
          )}
        </div>
        
        {/* Scroll to Top Button */}
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          ↑
        </button>
      </Container>
    </Container>
  );
}

export default Projects;
